import {
  Box,
  CircularProgress,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { makeStyles, styled } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { InfoIcon } from "../../shared/icon";
import VSNTypography from "../../shared/VSNTypography";
import { ApiContainer } from "../../utils/api";
import VSNToolTip from "../../shared/VSNToolTip";
import VSNButton from "../../shared/VSNButton";
import { toggleModelShow } from "../../shared/CustomFunc";

const useStyles = makeStyles((theme) => ({
  assessmentTableCard: {
    width: "100%",
    flex: 1,
    backgroundColor: "var(--white)",
    alignItems: "center",
    cursor: "move",
    padding: "15px 24px",
    "& .MuiStack-root": {
      color: "var(--gray)",
    },
    "& svg": {
      flex: "none",
    },
    "&:hover": {
      backgroundColor: "#f1f0f0",
    },
  },
  assessmentTableCards: {
    // minWidth: 1440,
    width: "100%",
    backgroundColor: "white",
  },
  assessmentTableCardsTitle: {
    alignItems: "center",
    "& p": {
      padding: "15px 24px",
      fontSize: 18,
      fontWeight: 600,
      flex: 1,
      "& svg": {
        verticalAlign: "middle",
      },
    },
  },
  assessmentList: {
    maxHeight: "400px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 8,
      padding: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 6,
      background: "#c7c5c5",
      border: "1px solid #ffffff",
    },
  },
  createButton: {
    "&.MuiButton-root": {
      // padding: "11px 25px",
      height: "40px",
      width: "56px",
      padding: 0,
      minWidth: "auto",
      "& svg": {
        height: 20,
      },
      "& .content": {
        fontWeight: 500,
        fontSize: 16,
        lineHeight: "18px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        [theme.breakpoints.down("md")]: {
          fontSize: 14,
        },
        "& .MuiStack-root": {
          gap: 10,
        },
      },
      [theme.breakpoints.down("md")]: {
        padding: "8px 12px",
      },
    },
  },
}));

const AntSwitch = styled(Switch)(({ theme }) => ({
  "&.MuiSwitch-root": {
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "var(--white)",
        "& + .MuiSwitch-track": {
          opacity: 1,
          background: "var(--Tertiary)",
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.3,
          background: "var(--Tertiary)",
        },
      },
      "& + .MuiSwitch-track": {
        background: "#00000040",
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: "all 0.4s ease-in-out",
    },
    "& .MuiSwitch-track": {
      borderRadius: 10,
      opacity: 1,
      boxSizing: "border-box",
    },
  },
}));

let count;
const Item = ({ item, classes, handleSwitchChange }) => {
  count += 1;

  return (
    <Stack direction="row" className={classes.assessmentTableCard}>
      <Stack flex={1}>
        <VSNTypography sx={{ whiteSpace: "normal" }}>
          {item?.waiver_name || ""}
        </VSNTypography>
      </Stack>

      <Stack direction="row" alignItems="start" gap={4} minWidth={180}>
        <AntSwitch
          sx={item?.nonChangeable ? { pointerEvents: "none" } : {}}
          disabled={item?.nonChangeable}
          checked={item?.is_required || false}
          onChange={(event) =>
            !item?.nonChangeable ? handleSwitchChange(event, item) : null
          }
          inputProps={{ "aria-label": "ant design" }}
        />
      </Stack>
    </Stack>
  );
};

export default function WaiversTab({ tableViewPermission, isOpen }) {
  count = 0;
  const classes = useStyles();
  const { api } = ApiContainer();
  const dispatch = useDispatch();
  const organization_original_id = useSelector(
    (state) => state?.app?.userData?.user_profile?.organization_original_id
  );
  const dataForAssessment = useSelector(
    (state) => state?.api?.dataForAssessment
  );

  const [isLoading, setIsLoading] = useState(true);
  const [waivers, setWaivers] = useState([]);

  const role = useSelector((state) => state?.app?.userData?.user_profile?.role);
  const activeTab = useSelector((state) => state?.api?.tabs?.["org-summary"]);
  const apiEndpoint =
    activeTab === "locations"
      ? `organization/${organization_original_id}/locations/${dataForAssessment?.location_original_id}`
      : activeTab === "departments"
      ? `organization/${organization_original_id}/locations/${dataForAssessment?.location_original_id}/departments/${dataForAssessment?.department_original_id}`
      : `organization/${organization_original_id}/locations/${
          dataForAssessment?.location_original_id
        }/departments/${dataForAssessment?.department_original_id}/member/${
          dataForAssessment?.user_id ?? dataForAssessment?.admins[0]?.id
        }`;

  useEffect(() => {
    const getWaiversAssessment = async () => {
      setIsLoading(true);

      const reportsData = await api({
        method: "GET",
        endPoint: `${apiEndpoint}/waivers/is-required`,
        urlencoded: false,
      });

      if (reportsData?.status && Array.isArray(reportsData?.data)) {
        setWaivers(reportsData.data);
      } else {
        setWaivers([]);
      }
      setIsLoading(false);
    };

    if (apiEndpoint && isOpen) {
      getWaiversAssessment();
    }
  }, [isOpen]);

  const onSwitchChange = async (event, item) => {
    const updatedWaiver = {
      ...item,
      is_required: event.target.checked,
      enabled: event.target.checked,
    };

    setWaivers((prevWaivers) =>
      prevWaivers?.map((waiver) =>
        waiver.waiver_id === item.waiver_id ? updatedWaiver : waiver
      )
    );
    await api({
      method: "POST",
      endPoint: `${apiEndpoint}/waivers/is-required`,
      data: {
        waiver_id: updatedWaiver.waiver_id,
        is_required: updatedWaiver.is_required,
      },
      showToastMessage: true,
      urlencoded: false,
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          textAlign: "center",
          height: 45,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={24} color="inherit" />
      </Box>
    );
  }

  return (
    <>
      <Box
        className={classes.assessmentTableCards}
        sx={{ marginBottom: "20px" }}
      >
        {waivers?.length > 0 ? (
          <>
            <Stack
              className={classes.assessmentTableCardsTitle}
              direction="row"
            >
              <Typography variant="body1">Waivers Name</Typography>
              <Stack direction="row" alignItems="center" gap={4} px={2}>
                <Typography variant="body1">
                  Required
                  <VSNToolTip
                    placement="top"
                    arrow
                    title="Mark waiver as required for all users."
                    slotProps={{
                      popper: {
                        sx: {
                          "& .MuiTooltip-tooltip": {
                            backgroundColor: "var(--white)",
                            boxShadow:
                              "0px 6.447px 8px 0px rgba(0, 0, 0, 0.10)",
                            color: "var(--blackGray)",
                            py: 1,
                            px: 1.5,
                            fontSize: 14,
                            borderRadius: 3,
                            "& .MuiTooltip-arrow": {
                              color: "var(--white)",
                            },
                          },
                        },
                      },
                    }}
                  >
                    <span>
                      {" "}
                      <InfoIcon />
                    </span>
                  </VSNToolTip>
                </Typography>

                <VSNButton
                  handleClick={() =>
                    dispatch(toggleModelShow("uploadWaiversDocumnet"))
                  }
                  className={classes.createButton}
                >
                  <FileUploadOutlinedIcon />
                </VSNButton>
              </Stack>
            </Stack>
            <Box className={classes.assessmentList}>
              {waivers?.map((item, index) => (
                <Item
                  key={index}
                  item={item}
                  draggable
                  classes={classes}
                  handleSwitchChange={(event, item) =>
                    onSwitchChange(event, item)
                  }
                  role={role}
                  tableViewPermission={tableViewPermission}
                />
              ))}
            </Box>
          </>
        ) : (
          <Typography variant="body1" sx={{ textAlign: "center", py: 2 }}>
            Waivers not found
          </Typography>
        )}
      </Box>
    </>
  );
}
